import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner, Alert, Card, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaBoxOpen, FaHourglassHalf, FaTruck, FaClipboardCheck } from "react-icons/fa";
import { userAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import "../../assets/css/TrackBooking.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import ClientChatBox from "../chat/ClientChatBox";


export default function BookingEmployeeTrack() {
  const { t } = useTranslation();
  const { booking_id, employeeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null
  );

  useEffect(() => {
    fetchTrackingData();
  }, [booking_id]);

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userAPI.employees.bookingTrack(employeeId, booking_id);
      if (response.success) {
        setTrackingData(response.data.data || response.data);
      } else {
        setError(response.message || t('general.failed_to_fetch_tracking'));
      }
    } catch (err) {
      setError(t('general.failed_to_fetch_tracking'));
      console.error('Error fetching tracking data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getStepIcon = (stepKey) => {
    const icons = {
      created: <FaCalendarAlt size={24} />,
      approved: <FaBoxOpen size={24} />,
      assigned: <FaHourglassHalf size={24} />,
      picked_up: <FaTruck size={24} />,
      delivered: <FaClipboardCheck size={24} />
    };
    return icons[stepKey] || <FaCalendarAlt size={24} />;
  };

  const getStepTitle = (stepKey, stepData) => {
    const titles = {
      created: t('general.order_created'),
      approved: t('general.order_approved'),
      assigned: t('general.order_assigned'),
      picked_up: t('general.order_picked_up'),
      delivered: t('general.order_delivered')
    };
    return translateTrackingText(stepData?.title) || titles[stepKey] || stepKey;
  };

  const translateTrackingText = (value) => {
    if (!value) {
      return '';
    }

    const text = String(value).trim();
    const translations = {
      Created: t('general.status_created'),
      created: t('general.status_created'),
      approved: t('general.status_approved'),
      assigned: t('general.status_assigned'),
      received: t('general.status_received'),
      completed: t('general.status_completed'),
      'Order Created': t('general.order_created'),
      'Order Approved': t('general.order_approved'),
      'Order Assigned': t('general.order_assigned'),
      'Order Picked Up': t('general.order_picked_up'),
      'Order Delivered': t('general.order_delivered'),
      'Order was created in the system.': t('general.order_created_in_system'),
      'Order has not been approved by admin yet.': t('general.order_not_approved'),
      'Order approved by admin.': t('general.order_approved_by_admin'),
      'Order has not been accepted by a driver yet.': t('general.order_not_assigned'),
      'Order accepted by driver.': t('general.order_accepted_by_driver'),
      'Order has not been picked up by the driver yet.': t('general.order_not_picked_up'),
      'Order has not been delivered yet.': t('general.order_not_delivered'),
      'Order picked up by driver.': t('general.order_picked_up_by_driver'),
      'Order delivered by driver.': t('general.order_delivered_by_driver'),
    };

    return translations[text] || text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" />
        <span className="ms-2">{t('general.loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="p-4">
        <Alert variant="danger">
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t('general.back')}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!trackingData) {
    return (
      <Container className="p-4">
        <Alert variant="warning">
          {t('general.no_tracking_data')}
          <div className="mt-3">
            <Button variant="outline-warning" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t('general.back')}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  const steps = Object.entries(trackingData.tracking_stages || {}).map(([key, data], index, array) => {
    const isActive = !!data?.status && data.status !== null;
    
    let lastActiveIndex = -1;
    for (let i = array.length - 1; i >= 0; i--) {
      if (!!array[i][1]?.status && array[i][1].status !== null) {
        lastActiveIndex = i;
        break;
      }
    }
    
    const isCurrent = !isActive && index === lastActiveIndex + 1;
    
    const dateInfo = data?.date ? formatDate(data.date) : { date: '', time: '' };
    
    return {
      key,
      title: getStepTitle(key, data),
      desc: translateTrackingText(data?.details || ''),
      location: data?.location || '',
      date: dateInfo.date,
      time: dateInfo.time,
      icon: getStepIcon(key),
      active: isActive,
      current: isCurrent,
      status: translateTrackingText(data?.status)
    };
  });

  return (
    <>
      <NavbarMenu />
      <Container className="shipment-container p-4">
        {/* Header */}
        <Card className="tracking-header border-0">
          <Card.Body className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                {t('general.booking_tracking')}: <span className="text-warning">{trackingData.booking_id}</span>
              </h4>
              <p className="mb-0 opacity-90">{t('general.tracking_details')}</p>
            </div>
            <Button className="back-btn" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t('general.back_to_details')}
            </Button>
          </Card.Body>
        </Card>

        {/* Horizontal Timeline */}
        <div className="timeline-horizontal">
          {steps.map((step, index) => (
            <div 
              key={step.key} 
              className={`timeline-item ${step.current ? 'current-item' : ''} ${step.active ? 'active' : ''}`}
            >
              {index < steps.length - 1 && (
                <div 
                  className={`timeline-connector ${step.active ? 'active' : ''}`}
                ></div>
              )}
              
              <div className={`timeline-icon ${step.current ? 'current' : step.active ? 'active' : 'inactive'}`}>
                {step.icon}
              </div>
              
              <div className="timeline-content">
                <h5 className={`timeline-title ${step.current ? 'current' : step.active ? 'active' : 'inactive'}`}>
                  {step.title}
                </h5>
                
                <p className="timeline-desc">{step.desc}</p>
                
                {step.location && (
                  <p className="timeline-location">{step.location}</p>
                )}
                
                {step.date && (
                  <div className="timeline-date-time">
                    <div className="timeline-date">{step.date}</div>
                    {step.time && (
                      <div className="timeline-time">{step.time}</div>
                    )}
                  </div>
                )}
                
                {step.status && (
                  <Badge 
                    bg={step.active ? 'success' : 'secondary'} 
                    className="timeline-badge"
                  >
                    {step.status}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </Container>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
